import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Unlock, ChevronUp } from "lucide-react";
import { holdApi } from "@/lib/tourEntities";
import { assetApi } from "@/lib/entities";
import { apiError } from "@/lib/errors";
import { ActionMenu } from "@/components/ActionMenu";
import { assetDisplay } from "@/lib/assetDisplay";

const LEVEL_STYLES = {
  soft: "bg-chrome-mist text-carbon border-coach-silver",
  first: "bg-amber-hold/15 text-carbon border-amber-hold",
  second: "bg-coach-blue/10 text-coach-blue border-coach-blue",
};

const RELEASE_ROLES = ["super_admin", "leasing_sales", "ops_dispatch"];
// Backend rule (services/tour_service.HoldsService.promote): only `second`
// holds may be promoted — they become `first`. `soft` and any level with
// no higher valid state hide the Promote action. Kept in sync with
// require_roles on POST /api/holds/{id}/promote.
const PROMOTE_ROLES = ["super_admin", "leasing_sales"];
const CAN_PROMOTE_FROM = new Set(["second"]);

/**
 * Tour-scoped Holds tab. Lists active holds attached to the current tour,
 * with a standard ActionMenu on each row. Release is exposed as a menu
 * item (no redundant inline button) — matches Draft Quotes / Draft
 * Contracts / Service WOs pattern.
 */
export default function HoldsTab({ tourId, canWrite = true, userRole }) {
  const [holds, setHolds] = useState([]);
  const [assets, setAssets] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const rows = await holdApi.list({ tour_id: tourId });
      setHolds(rows);
      const missing = Array.from(new Set(rows.map((r) => r.asset_id))).filter(
        (id) => id && !assets[id],
      );
      if (missing.length) {
        const loaded = await Promise.all(
          missing.map((id) => assetApi.get(id).catch(() => null)),
        );
        setAssets((prev) => {
          const next = { ...prev };
          loaded.forEach((a) => { if (a) next[a.id] = a; });
          return next;
        });
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, [tourId]);

  const canRelease = canWrite && RELEASE_ROLES.includes(userRole);
  const canPromoteRole = canWrite && PROMOTE_ROLES.includes(userRole);

  const release = async (hold) => {
    const reason = window.prompt(
      `Release ${hold.hold_level} hold?\n\nOptional reason:`,
      "",
    );
    if (reason === null) return; // cancelled
    setBusyId(hold.id);
    try {
      await holdApi.release(hold.id, reason || "released via tour holds tab");
      toast.success("Hold released");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const promote = async (hold) => {
    setBusyId(hold.id);
    try {
      const promoted = await holdApi.promote(hold.id);
      toast.success(`Hold promoted to ${promoted.hold_level}`);
      load();
    } catch (e) {
      // Surface the backend's own validation message (e.g. "first_hold_still_active").
      toast.error(apiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (holds.length === 0) {
    return (
      <div
        className="bg-white border border-coach-silver rounded-md p-5 text-[13px] text-steel"
        data-testid="holds-empty"
      >
        No active holds on this tour.
      </div>
    );
  }

  return (
    <div className="bg-white border border-coach-silver rounded-md" data-testid="tour-holds-list">
      <table className="w-full text-[13px]">
        <thead className="border-b border-coach-silver bg-road-white">
          <tr className="text-left">
            <th className="px-4 py-2 text-[11px] uppercase tracking-widest text-steel font-medium">Coach / Trailer</th>
            <th className="px-4 py-2 text-[11px] uppercase tracking-widest text-steel font-medium">Level</th>
            <th className="px-4 py-2 text-[11px] uppercase tracking-widest text-steel font-medium">Dates</th>
            <th className="px-4 py-2 text-[11px] uppercase tracking-widest text-steel font-medium">Notes</th>
            <th className="px-4 py-2 w-10" />
          </tr>
        </thead>
        <tbody>
          {holds.map((h) => {
            const asset = assets[h.asset_id];
            const levelClass = LEVEL_STYLES[h.hold_level] || LEVEL_STYLES.soft;
            return (
              <tr key={h.id} data-testid={`tour-hold-row-${h.id}`}
                  className="border-b border-coach-silver/60 last:border-0">
                <td className="px-4 py-2 text-carbon">
                  {asset ? assetDisplay(asset) : <span className="font-mono text-steel">{h.asset_id.slice(0, 8)}…</span>}
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-block text-[11px] uppercase tracking-wide border rounded-sm px-2 py-0.5 ${levelClass}`}
                        data-testid={`tour-hold-level-${h.id}`}>
                    {h.hold_level}
                  </span>
                </td>
                <td className="px-4 py-2 text-carbon tabular">
                  {h.start_date} → {h.end_date}
                </td>
                <td className="px-4 py-2 text-steel truncate max-w-[280px]">
                  {h.notes || <span className="italic">—</span>}
                </td>
                <td className="px-2 py-2 text-right">
                  <ActionMenu
                    testid={`tour-hold-menu-${h.id}`}
                    disabled={busyId === h.id}
                    customActions={[
                      ...(canPromoteRole && CAN_PROMOTE_FROM.has(h.hold_level) ? [{
                        key: "promote",
                        label: "Promote",
                        icon: <ChevronUp size={14} />,
                        onClick: () => promote(h),
                        testid: `tour-hold-promote-${h.id}`,
                      }] : []),
                      ...(canRelease ? [{
                        key: "release",
                        label: "Release",
                        icon: <Unlock size={14} />,
                        danger: true,
                        onClick: () => release(h),
                        testid: `tour-hold-release-${h.id}`,
                      }] : []),
                    ]}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
